"use client";
import { useTranslations } from "@zapier/i18n";

import { ChangeEvent, useState } from "react";

import {
  Button,
  Field,
  Link,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  TextInput,
} from "@zapier/design-system";

import styles from "./ZapRenameModal.module.css";
import { getMessageFromNetworkError, useRenameZap } from "@zapier/client";
import { useNotification, getEditZapUrl } from "@zapier/utils";
import { useAutoSelectInputText } from "../../../hooks/useAutoSelectInputText";

interface Props {
  id?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialValue?: string;
  folderId?: string; // used for cache invalidation
}

interface ContentProps extends Props {
  onSubmit: (title: string) => void;
}

// Emojis are not supported in zap titles because flow_node.title in MySQL
// uses utf8 (3-byte) charset which silently truncates 4-byte characters.
// Regional_Indicator covers flag emojis (🇺🇸, 🇬🇧) which are also 4-byte.
const EMOJI_REGEX = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u;

function ZapRenameModalContent({
  initialValue,
  setIsOpen,
  onSubmit,
}: ContentProps) {
  const t = useTranslations("zap-rename-modal");
  const nameInputIdentifier = "rename-zap-name";

  const [value, setValue] = useState(initialValue || "");
  const isLengthValid = value.length > 0 && value.length <= 255;
  const hasEmoji = EMOJI_REGEX.test(value);
  const validationError = hasEmoji
    ? t("validation.emoji-not-supported")
    : undefined;
  const isValid = isLengthValid && !hasEmoji;

  useAutoSelectInputText({
    isOpen: true,
    inputSelector: `#${nameInputIdentifier}`,
    value: initialValue,
  });

  const submit = () => {
    if (!isValid) {
      return;
    }
    if (value !== initialValue) {
      onSubmit(value);
    }
  };

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={submit}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (!isValid || value === initialValue) return;
          return event.key === "Enter" && submit();
        }}
      >
        <ModalDialogHeader textAlign="start">
          {t("header.rename-title", { initialValue: initialValue || "" })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          <div className={styles.body}>
            <Field
              label={t("form.name-label")}
              inputId={nameInputIdentifier}
              isRequired={true}
              error={validationError}
              renderInput={(inputProps) => (
                <TextInput
                  {...inputProps}
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the input when the modal opens
                  autoFocus
                  id={nameInputIdentifier}
                  isErrored={!isLengthValid || hasEmoji}
                  defaultValue={initialValue}
                  isFullWidth={true}
                  maxLength={255}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setValue(event.target?.value || "")
                  }
                  placeholder={t("form.name-placeholder")}
                />
              )}
            />
          </div>
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button
            onPress={submit}
            isDisabled={!isValid || value === initialValue}
          >
            {t("buttons.save")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export function ZapRenameModal(props: Props) {
  const t = useTranslations("zap-rename-modal");

  const { id, isOpen, setIsOpen, folderId } = props;
  const { createNotification } = useNotification();
  const { rename } = useRenameZap({ id, folderId });

  /** This callback needs to happen outside of the modal so that it can
   * still display a notification when the request completes after the modal has closed.
   */
  const submit = (title: string) => {
    if (!id) {
      return;
    }
    rename(
      { title, zapId: id },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t.rich("notifications.rename-success", {
                  title,
                  editorLink: (chunks) => (
                    <Link href={getEditZapUrl(id)}>{chunks}</Link>
                  ),
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error) => {
          createNotification({
            title: t("errors.failed-to-rename"),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return <ZapRenameModalContent {...props} onSubmit={submit} />;
}
